/**
 * Gravacao e transcricao de audio no chat do assistente (rodada 4 do bloco
 * "anexos e voz", ADR-0015 — fecha o bloco).
 *
 * Grava no client via `MediaRecorder` + `getUserMedia`, envia o audio como
 * data URL base64 para POST /api/assistant/transcribe (mesmo transporte dos
 * anexos de imagem/documento — ver assistant-attachments.ts) e devolve o
 * texto para o widget colocar no INPUT do usuario revisar/editar. NUNCA
 * envia a mensagem automaticamente.
 *
 * Espelha os tetos do servidor (apps/api/.../audio-transcription.ts):
 * MAX_RECORDING_SECONDS ~3min, MAX_RECORDING_BYTES ~10MB.
 */

import { authedFetch } from './authed-fetch';

export const MAX_RECORDING_SECONDS = 180;
export const MAX_RECORDING_BYTES = 10 * 1024 * 1024;

/**
 * Candidatos de mimetype para o MediaRecorder, em ordem de preferencia.
 * Chrome/Edge/Firefox suportam webm+opus (o melhor par tamanho/qualidade
 * para fala); Safari so suporta mp4 (AAC). O servidor aceita os dois grupos
 * (ver ALLOWED_AUDIO_MIME_TYPES em audio-transcription.ts).
 */
const CANDIDATE_AUDIO_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
] as const;

/** Primeiro mimetype da lista que o MediaRecorder deste browser suporta, ou undefined. */
export function pickSupportedAudioMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return undefined;
  }
  return CANDIDATE_AUDIO_MIME_TYPES.find((type) => {
    try {
      return MediaRecorder.isTypeSupported(type);
    } catch {
      return false;
    }
  });
}

/**
 * True quando o browser suporta gravar audio num formato que o servidor
 * aceita. Usado pelo widget para SO OFERECER o botão de microfone quando a
 * gravacao realmente funciona (getUserMedia exige HTTPS ou localhost — em
 * contexto inseguro `navigator.mediaDevices` nem existe, entao esta funcao
 * ja devolve false sem checagem extra).
 */
export function isVoiceRecordingSupported(): boolean {
  const hasGetUserMedia =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function';
  const hasMediaRecorder = typeof MediaRecorder !== 'undefined';
  return hasGetUserMedia && hasMediaRecorder && pickSupportedAudioMimeType() !== undefined;
}

/** Formata segundos como "m:ss" para o indicador de gravacao (ex: 65 -> "1:05"). */
export function formatElapsed(totalSeconds: number): string {
  const clamped = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(clamped / 60);
  const seconds = clamped % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Não foi possível processar o áudio gravado.'));
    reader.readAsDataURL(blob);
  });
}

export interface TranscribeAudioResult {
  text: string;
}

/** Extrai uma mensagem de erro amigavel do corpo de resposta do backend (JSON {message} ou texto). */
async function extractErrorMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { message?: string | string[] };
    if (Array.isArray(body.message) && body.message.length > 0) return body.message.join(' ');
    if (typeof body.message === 'string' && body.message) return body.message;
  } catch {
    // Corpo nao-JSON: cai no fallback abaixo.
  }
  return 'Não foi possível transcrever o áudio. Tente novamente.';
}

/**
 * Envia o audio gravado para POST /api/assistant/transcribe e devolve o
 * texto transcrito. O audio e efemero: convertido para data URL so para o
 * upload, nunca gravado em disco/storage no client.
 */
export async function transcribeRecordedAudio(
  blob: Blob,
  durationSeconds: number,
): Promise<TranscribeAudioResult> {
  const audio = await blobToDataUrl(blob);

  const res = await authedFetch('/api/assistant/transcribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ audio, durationSeconds }),
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res));
  }

  const data = (await res.json()) as { text?: string };
  return { text: data.text ?? '' };
}
